// ConfirmationToast - Socket notification toast component
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface ConfirmationToastProps {
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
    confirmText?: string;
    autoCloseTime?: number | false;
}

// Accent color for icon and button
const ACCENT_GRADIENT = 'linear-gradient(135deg, #4361ee 0%, #7c3aed 100%)';

// Multi-color gradient for the progress bar (matching the old colorful loader)
const PROGRESS_GRADIENT = 'linear-gradient(90deg, #34d399, #3b82f6, #a855f7, #f43f5e, #fb923c)';

// Inject a custom style for the progress bar gradient (react-toastify progressStyle doesn't support gradients well on the bar element)
const injectProgressBarStyle = (toastId: string | number) => {
    // Use a small timeout to let the toast render first
    setTimeout(() => {
        const styleId = 'confirmation-toast-progress-style';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                .Toastify__progress-bar--wrp {
                    height: 5px !important;
                }
                .confirmation-toast-custom .Toastify__progress-bar {
                    background: ${PROGRESS_GRADIENT} !important;
                    height: 5px !important;
                }
            `;
            document.head.appendChild(style);
        }
    }, 50);
};

// Function to show confirmation toast
const ConfirmationToast = ({
    message,
    onConfirm,
    onCancel,
    confirmText = "Refresh",
    autoCloseTime = 10000,
}: ConfirmationToastProps) => {
    
    // Track if user has made a choice to prevent double execution
    let userChoiceMade = false;
    
    const handleConfirm = () => {
        if (!userChoiceMade) {
            userChoiceMade = true;
            toast.dismiss();
            onConfirm();
        }
    };

    const handleClose = () => {
        if (!userChoiceMade) {
            userChoiceMade = true;
            toast.dismiss();
            if (onCancel) {
                onCancel();
            }
        }
    };

    const ConfirmationToastContent = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            minWidth: '280px',
            padding: '12px 14px 8px',
        }}>
            {/* Header: Info icon + Action Required + Close button */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                }}>
                    {/* Info icon - gradient color */}
                    <div style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        background: ACCENT_GRADIENT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <span style={{
                            color: '#fff',
                            fontWeight: '700',
                            fontSize: '13px',
                            lineHeight: 1,
                        }}>i</span>
                    </div>
                    <span style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#1a1a2e',
                    }}>Action Required</span>
                </div>
                {/* Close icon */}
                <button
                    onClick={handleClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        transition: 'background-color 0.2s ease',
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.08)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Close"
                >
                    <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                        <path d="M1 1L13 13M13 1L1 13" stroke="#999" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>
            </div>

            {/* Message */}
            <div style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#555',
                lineHeight: '1.45',
                paddingLeft: '32px',
            }}>
                {message}
            </div>

            {/* Refresh button with gradient */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                paddingTop: '0px',
                marginBottom: '6px',
            }}>
                <button
                    onClick={handleConfirm}
                    style={{
                        background: ACCENT_GRADIENT,
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        padding: '5px 18px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        letterSpacing: '0.3px',
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.opacity = '0.9';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.opacity = '1';
                    }}
                >
                    {confirmText}
                </button>
            </div>
        </div>
    );

    // Show the toast with custom content
    const toastId = toast(<ConfirmationToastContent />, {
        position: "bottom-left",
        autoClose: autoCloseTime,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        closeButton: false,
        icon: false,
        className: 'confirmation-toast-custom',
        style: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
            border: '1px solid #e8eaf0',
            padding: '0',
            overflow: 'hidden',
        },
        progressStyle: {
            background: PROGRESS_GRADIENT,
        },
        onClose: () => {
            // When toast auto-closes via timer or close icon, do NOT auto-refresh
            if (!userChoiceMade) {
                userChoiceMade = true;
                if (onCancel) {
                    onCancel();
                }
            }
        }
    });

    // Inject the multi-color progress bar style
    injectProgressBarStyle(toastId);

    console.log("Toast ID:", toastId);
    return null;
};

export default ConfirmationToast;
